import React, { useEffect, useMemo, useState } from "react";
import { thirteenWeekCashFlowApi } from "../../helpers/thirteenWeekCashFlowApi";
import { dateLabel, longDateLabel, money } from "./money";

const SLACK_ARCHIVES_URL = "https://kingsloot.slack.com/archives";

const inflowRows = [
  ["operating_revenue", "Operating revenue"],
  ["other_inflow", "Other cash in"],
];
const outflowRows = [
  ["sales_tax", "Sales tax"],
  ["ap_operating", "AP – Operating"],
  ["ap_inventory", "AP – Inventory"],
  ["credit_card", "Credit card"],
  ["payroll", "Payroll"],
  ["owner_distribution", "Owner distributions"],
  ["debt", "Debt repay"],
  ["other_outflow", "Other cash out"],
];

const timeLabel = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/Denver",
    timeZoneName: "short",
  });
};

const readoutDateTimeLabel = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "America/Denver",
  }).formatToParts(date).map(({ type, value: part }) => [type, part]));
  return `${parts.month}/${parts.day}/${parts.year} ${parts.hour}:${parts.minute} ${parts.dayPeriod}`;
};

const readoutLabel = (readout) => {
  if (!readout) return "No readout posted for this version";
  const postedAt = readoutDateTimeLabel(readout.postedAt);
  return `Readout created ${postedAt} · ${readout.status || "Status unavailable"}`;
};

const versionsOf = (snapshot) => {
  const readouts = Array.isArray(snapshot.readouts) ? snapshot.readouts : [];
  return [
    { ...snapshot, readouts, key: `${snapshot.weekStart}|${snapshot.generatedAt}`, replaced: false },
    ...(snapshot.replacedVersions || []).map((version) => ({
      ...version,
      readouts,
      key: `${version.weekStart}|${version.generatedAt}`,
      replaced: true,
    })),
  ];
};

const slackReadoutUrl = (post, threadTs) => {
  const channel = String(post?.channel || "");
  const timestamp = String(post?.ts || "");
  if (!channel || !timestamp) return null;
  const url = `${SLACK_ARCHIVES_URL}/${encodeURIComponent(channel)}/p${timestamp.replace(".", "")}`;
  return threadTs
    ? `${url}?thread_ts=${encodeURIComponent(threadTs)}&cid=${encodeURIComponent(channel)}`
    : url;
};

const versionLabel = (version) =>
  `Week of ${longDateLabel(version.weekStart)} · saved ${timeLabel(version.generatedAt)}${version.replaced ? " (replaced)" : ""}${version.excluded ? ` · Excluded as baseline: ${version.excluded.reason || "No reason provided"}` : ""}`;

const tone = (value) => (Number(value) < 0 ? "text-red-700" : "");

export default function PublishedForecastsTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedKey, setSelectedKey] = useState("");
  const [compareKey, setCompareKey] = useState("");

  useEffect(() => {
    let cancelled = false;
    thirteenWeekCashFlowApi
      .getPublishedForecasts()
      .then((response) => {
        if (cancelled) return;
        setData(response);
        const versions = (response.snapshots || []).flatMap(versionsOf);
        setSelectedKey(versions[0]?.key || "");
        setCompareKey(versions.find((version) => !version.replaced && version.key !== versions[0]?.key)?.key || "");
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError?.response?.data?.error || "Published forecasts could not be loaded");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const versions = useMemo(() => (data?.snapshots || []).flatMap(versionsOf), [data]);
  const selected = versions.find((version) => version.key === selectedKey);
  const compare = versions.find((version) => version.key === compareKey);

  if (error) return <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>;
  if (!data) return <div className="p-4 text-sm text-gray-500">Loading published forecasts…</div>;
  if (!versions.length) {
    return (
      <div className="rounded-md border border-gray-200 bg-white p-4 text-sm text-gray-600">
        No published forecasts yet. Eadin saves each Monday&apos;s forecast when the weekly report posts.
      </div>
    );
  }

  const compareWeeks = new Map((compare?.weekly || []).map((week) => [week.start, week]));
  const rows = [
    { label: "Beginning cash", value: (week) => week.beginningCash, strong: true },
    ...inflowRows.map(([key, label]) => ({ label, value: (week) => week.values?.[key] })),
    { label: "Total cash in", value: (week) => week.totalCashIn, strong: true },
    ...outflowRows.map(([key, label]) => ({ label, value: (week) => -Number(week.values?.[key] || 0) })),
    { label: "Total cash out", value: (week) => -Number(week.totalCashOut || 0), strong: true },
    { label: "Net cash flow", value: (week) => week.netCashFlow, strong: true },
    { label: "Ending cash", value: (week) => week.endingCash, strong: true },
  ];
  const revenue = selected?.week0RevenueByChannel || {};

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 rounded-md border border-gray-200 bg-white p-3">
        <label className="text-xs font-medium text-gray-600">
          Published forecast
          <select
            value={selectedKey}
            onChange={(event) => setSelectedKey(event.target.value)}
            className="mt-1 block h-8 min-w-[22rem] rounded border-gray-200 text-sm"
          >
            {versions.map((version) => (
              <option key={version.key} value={version.key}>{versionLabel(version)}</option>
            ))}
          </select>
        </label>
        <label className="text-xs font-medium text-gray-600">
          Compare to
          <select
            value={compareKey}
            onChange={(event) => setCompareKey(event.target.value)}
            className="mt-1 block h-8 min-w-[22rem] rounded border-gray-200 text-sm"
          >
            <option value="">No comparison</option>
            {versions.filter((version) => version.key !== selectedKey).map((version) => (
              <option key={version.key} value={version.key}>{versionLabel(version)}</option>
            ))}
          </select>
        </label>
      </div>
      {selected && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            ["Opening cash", selected.openingCash],
            ["Effective Capital", selected.effectiveCapital],
            ["First-week Shopify deposits", revenue.shopify],
            ["First-week Amazon deposits", revenue.amazon],
          ].map(([label, value]) => (
            <div key={label} className="rounded-md border border-gray-200 bg-white p-3">
              <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
              <div className={`mt-1 text-lg font-semibold tabular-nums ${tone(value)}`}>{money(value, { dash: "$0" })}</div>
            </div>
          ))}
        </div>
      )}
      {selected && (
        <p className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
          {readoutLabel(selected.readout)}
        </p>
      )}
      {selected && (
        <div className="space-y-2 rounded-md border border-gray-200 bg-white p-3">
          <h3 className="text-sm font-semibold text-gray-800">Readouts posted for this week</h3>
          {selected.readouts.length ? selected.readouts.map((readout, index) => (
            <div
              key={`${readout.postedAt || "readout"}|${index}`}
              className="border-t border-gray-100 pt-2 text-sm text-gray-700"
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-medium">{readout.correctionNotice ? "Correction" : "Readout"}</span>
                <span>created {readoutDateTimeLabel(readout.postedAt)}</span>
                <span>· {readout.status || "Status unavailable"}</span>
                {readout.pdfAttached && <span>· PDF attached</span>}
                {(readout.slackPosts || []).map((post, postIndex) => {
                  const href = slackReadoutUrl(post, readout.threadTs);
                  return href ? (
                    <a
                      key={`${post.channel}|${post.ts}|${postIndex}`}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-blue-700 underline"
                    >
                      Open in Slack
                    </a>
                  ) : null;
                })}
                {readout.savedAsVersion === false && (
                  <span>Not saved as a new forecast version</span>
                )}
              </div>
              {readout.correctionNotice && (
                <p className="mt-1 text-gray-600">{readout.correctionNotice}</p>
              )}
            </div>
          )) : (
            <p className="border-t border-gray-100 pt-2 text-sm text-gray-500">
              No readouts recorded for this week
            </p>
          )}
        </div>
      )}
      {selected?.excluded && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Excluded as baseline: {selected.excluded.reason || "No reason provided"}
        </p>
      )}
      {selected && (
        <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
          <table className="min-w-full text-[12px]">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-gray-600">
                <th className="sticky left-0 bg-gray-50 px-2 py-2 text-left font-medium">
                  {compare ? `Saved ${dateLabel(selected.generatedAt)} vs ${dateLabel(compare.generatedAt)}` : `Saved ${dateLabel(selected.generatedAt)}`}
                </th>
                {(selected.weekly || []).map((week) => (
                  <th key={week.start} className="px-2 py-2 text-right font-medium">{dateLabel(week.start, { month: "2-digit", day: "2-digit" })}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className={`border-t border-gray-100 ${row.strong ? "font-semibold" : ""}`}>
                  <td className="sticky left-0 bg-white px-2 py-1.5 text-left">{row.label}</td>
                  {(selected.weekly || []).map((week) => {
                    const value = row.value(week);
                    const prior = compareWeeks.get(week.start);
                    const delta = prior ? Number(value || 0) - Number(row.value(prior) || 0) : null;
                    return (
                      <td key={week.start} className="px-2 py-1.5 text-right tabular-nums">
                        <span className={tone(value)}>{money(value)}</span>
                        {delta != null && Math.round(delta) !== 0 && (
                          <span className={`block text-[11px] font-normal ${delta < 0 ? "text-red-700" : "text-emerald-700"}`}>
                            {money(delta, { signed: true })}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {compare && (
        <p className="text-[11px] text-gray-500">
          Small numbers under each cell show the change from the forecast saved {longDateLabel(compare.generatedAt)}, matched by week. Weeks the other forecast didn&apos;t cover show no change.
        </p>
      )}
    </section>
  );
}
